import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center py-12 px-6"
      style={{
        backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/273d484d-85a8-4eaa-9e45-65aa50be200c/images/dbbd793f1124d050f1df1002a34be6839920e2b2c57eb2c411c5c4eef00d1c7d.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="absolute inset-0 bg-[#FDFBF7]/70"></div>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-md"
      >
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-[#5C6056] hover:text-[#2B2E26] transition-colors mb-8 font-body"
          data-testid="back-to-home"
        >
          <ArrowLeft size={18} />
          Back to Home
        </Link>

        <div className="bg-white/90 backdrop-blur-sm p-8 md:p-10 rounded-2xl border border-[#E6E2D6] shadow-lg">
          <div className="text-center mb-8">
            <h1 className="font-heading text-3xl font-medium text-[#2B2E26] mb-2" data-testid="login-title">
              Welcome Back
            </h1>
            <p className="font-body text-[#5C6056]">
              Sign in to continue your prayer ministry
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm font-body" data-testid="login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div>
              <label className="block text-sm font-body text-[#5C6056] mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="organic-input"
                placeholder="your@email.com"
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-body text-[#5C6056] mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="organic-input pr-10"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#8A8D86] hover:text-[#2B2E26]"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="font-body text-[#5C6056]">
              Don't have an account?{' '}
              <Link to="/register" className="text-[#5A6E4B] hover:text-[#4C5E3F] font-medium" data-testid="register-link">
                Join as Prayer Warrior
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
